import { HimmaContext } from '../context/HimmaContext';
import { useState, useCallback, useContext } from 'react';
import {
  AddOrEditItemRequestSchema,
  DeleteSubAdminsRequest,
  ToggleSubAdminStatusRequest,
  BulkUploadItemAddedByEnum,
} from '../client-sdk';

export const useStoreItemApi = () => {
  const api = useContext(HimmaContext)!;
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const addStoreItem = useCallback(
    async (storeItemData: AddOrEditItemRequestSchema): Promise<any> => {
      setLoading(true);
      setError(null);

      try {
        const response = await api.addItem(storeItemData);
        if (response?.data) {
          return response.data;
        }
        throw new Error('Failed to create item: No response data');
      } catch (err: unknown) {
        if (err instanceof Error) {
          setError(err.message);
        } else {
          setError('An unexpected error occurred');
        }
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [api]
  );

  const getAllStoreItem = useCallback(
    async (query: any) => {
      setLoading(true);
      setError(null);
      try {
        // Only include search parameter if it has a value
        const searchParam =
          query.search && query.search.trim() !== '' ? query.search : undefined;
        const storeTypeParam =
          query.storeType && String(query.storeType).trim() !== '' ? query.storeType : undefined;
        const response = await api.getAllItems(
          query.page,
          query.limit,
          query.sortBy,
          query.sortOrder,
          undefined, // merchantId
          query.storeId,
          storeTypeParam,
          searchParam,
          query.stockAvailability !== 'All' ? query.stockAvailability : undefined,
          query.addedBy
        );
        return response.data;
      } catch (err) {
        setError(err instanceof Error ? err.message : 'An error occurred');
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [api]
  );

  const blockOrUnblockStoreItemById = useCallback(
    async (query: any) => {
      setLoading(true);
      setError(null);
      try {
        const toggleSubAdminStatusRequest: ToggleSubAdminStatusRequest = {
          ids: Array.isArray(query.id) ? query.id : [query.id],
          status: query.status,
        };
        const response = await api.toggleItemStatus(
          toggleSubAdminStatusRequest
        );
        return response.data;
      } catch (err) {
        setError(err instanceof Error ? err.message : 'An error occurred');
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [api]
  );

  const deleteStoreItemById = useCallback(
    async (query: string[]) => {
      setLoading(true);
      setError(null);
      try {
        const deleteCustomer: DeleteSubAdminsRequest = {
          ids: query,
        };
        const response = await api.deleteItems(deleteCustomer);
        return response.data;
      } catch (err) {
        setError(err instanceof Error ? err.message : 'An error occurred');
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [api]
  );

  const editStoreItem = useCallback(
    async (itemId: any, storeItemData: AddOrEditItemRequestSchema) => {
      setLoading(true);
      setError(null);
      try {
        const response = await api.updateItem(itemId, storeItemData);
        return response.data;
      } catch (err) {
        setError(err instanceof Error ? err.message : 'An error occurred');
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [api]
  );

  const bulkUploadItem = useCallback(
    async (
      storeId: string,
      addedBy: BulkUploadItemAddedByEnum,
      file: File,
      merchantId?: string
    ): Promise<any> => {
      setLoading(true);
      setError(null);
      try {
        const response = await api.bulkUploadItem(storeId, addedBy, file, merchantId);
        return response.data;
      } catch (err) {
        setError(err instanceof Error ? err.message : 'An error occurred');
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [api]
  );

  const getBulkUploadStatus = useCallback(
    async (jobId: string): Promise<any> => {
      setLoading(true);
      setError(null);
      try {
        const response = await api.getBulkUploadStatus(jobId);
        return response.data;
      } catch (err) {
        setError(err instanceof Error ? err.message : 'An error occurred');
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [api]
  );

  const downloadBulkUploadTemplate = useCallback(
    async (storeId: string): Promise<any> => {
      setLoading(true);
      setError(null);
      try {
        const response = await api.downloadBulkUploadTemplate(storeId, {
          headers: {
            accept: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
          },
          responseType: 'blob' as any,
        });
        // Handle file download - API only supports XLSX format
        const blob = new Blob([response.data], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        });        
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `bulk_upload_template.xlsx`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        return response.data;
      } catch (err) {
        setError(err instanceof Error ? err.message : 'An error occurred');
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [api]
  );

  return {
    loading,
    error,
    addStoreItem,
    editStoreItem,
    getAllStoreItem,
    blockOrUnblockStoreItemById,
    deleteStoreItemById,
    bulkUploadItem,
    getBulkUploadStatus,
    downloadBulkUploadTemplate,
  };
};
